import { HealthInsurance } from '@/models/forms/backend-types/PersonalData.ts';
import { InsuranceType } from '@/models/insurance.ts';

export interface Patient {
    instance_id: string;
    user_id: string; // dc_user_id
    is_registered?: boolean;
    eterno_user_id?: string; // eterno user id or instance_id::pms_user_id for unregistered users
    primary_email?: string;

    name: {
        first_name: string; // firstname
        middle_name?: string; // middlename
        last_name: string; // lastname
        title?: string; // academic title, e.g. "Dr.", "Prof. Dr." (distinct from salutation)
        salutation: string; // [ "MR", "MS", "NONE" ]
        gender: string; // [  "", "MALE", "FEMALE", "VARIOUS", "UNKNOWN", "UNDEFINED" ]
    };

    account: {
        date_of_birth: string; // "1985-11-13T09:00:00.000Z"

        active_case_folder_id?: string;
        patient_number?: string;
    };

    contact: {
        emails: string[];
    };

    addresses: PatientAddress[];
    communications: PatientComms[];
    insurances: PatientInsurance[];

    is_data_admission_to_pvs_approved?: boolean;

    other_locations: {
        is_access_enabled?: boolean;
        ids?: string[];
    };

    sync_state: string; // UPTODATE, OUTDATED, VERIFIED_MISSING

    meta: {
        created_at: string;
        updated_at: string;

        patient_since?: string;
        synced_at: string;
    };
}

export interface PatientAddress {
    id: string;
    type: string; // [ "OFFICIAL", "POSTBOX", "POSTAL", "BILLING", "VISIT", "EMPLOYER" ]

    street?: string; // optional for Doc Cirrus
    house_number?: string;
    addon?: string; // Adresszusatz
    city: string;
    zip: string;
    country: string;
    country_code: string; // ISO 3166-1 alpha-2 e.g: 'DE'
}

export interface PatientComms {
    id: string;
    type: string; // [ "PHONEPRIV", "FAXPRIV", "MOBILEPRIV", "PHONEJOB", "FAXJOB", "MOBILEJOB", "EMAILPRIV",
    // "EMAILJOB", "FACEBOOK", "GOOGLE", "OTHER", "LINKEDIN", "XING", "URL", "PHONEEXT", "PHONEEMERGENCY" ]

    value: string;
    preferred?: boolean;
    confirmed?: boolean;
    confirmation_needed?: boolean;
}

export interface PatientInsurance {
    id: string;
    type: string; // [ "PUBLIC", "PUBLIC_A", "PRIVATE", "PRIVATE_A", "PRIVATE_CH", "PRIVATE_CH_IVG",
    // "PRIVATE_CH_MVG", "PRIVATE_CH_UVG", "PRIVATE_CH_VVG", "SELFPAYER", "BG" ]

    name: string;
    insurance_print_name: string;
    company_id: string;
    number: string;
    membership_type: string; // [ 1, 3, 5]

    ktab: string; // KTAB
    group_id: string; // insuranceGroup ("00")

    fee_schedule: string; // "1"
    expiry_date: string; // expiryDate ("2028-01-04T15:47:22.641Z")

    location_id?: string;

    card_swipe_date: string; // timestamp when insurance card was last swiped
}

export interface NormalizedPatient extends Patient {
    _normalized: {
        patient_number: string;
        primary_email: string;
        first_name: string;
        last_name: string;
        date_of_birth: string;
    };
}

export interface PatientPersonalData {
    salutation?: string; // [ "NONE", "MR", "MS" ] (Anrede)
    gender?: string;
    title?: string;
    first_name: string;
    middle_name?: string;
    last_name: string;
    date_of_birth: string;
    email?: string;
    phone?: string;
    landline_phone?: string;
}

export interface PatientContactData {
    street?: string;
    house_number?: string;
    addon?: string; // Adresszusatz
    zip: string;
    city: string;
    country_code: string; // ISO 3166-1 alpha-2 e.g: 'DE'
}

export interface CreateDocCirrusPatient {
    user: {
        personal: PatientPersonalData;
        contact?: PatientContactData;
        health_insurances?: Omit<HealthInsurance, 'insurance_id' | 'insurance_company'>[];
        occupation?: string;
    };
    location_id: string;
    is_data_admission_to_pvs_approved: boolean;
}

export interface UpdatePatient {
    personal?: PatientPersonalData;
    contact?: PatientContactData;
    insurances: UpdatePatientInsurance[];
}

export interface UpdatePatientInsurance {
    id: string;
    type?: InsuranceType;
    name?: string;
}

export type BasePatientFormData = {
    academic_title?: string;
    salutation?: string; // Anrede: 'NONE' | 'MR' | 'MS' (default 'NONE')
    gender?: string; // Geschlecht: '' | 'MALE' | 'FEMALE' | 'VARIOUS' | 'UNKNOWN' | 'UNDEFINED'
    first_name: string;
    last_name: string;
    date_of_birth: string;
    street?: string; // Adresse (optional)
    house_number?: string; // Hausnummer (optional)
    addon?: string; // Adresszusatz
    postal_code: string;
    city: string;
    country_code: string; // ISO 3166-1 alpha-2 e.g: 'DE'
    email?: string;
    phone?: string;
    landline_phone?: string;
};

// Type for creating new patients
export type PatientFormData = BasePatientFormData & {
    instance_id: string;
    insurance_type: InsuranceType;
    location_id: string; // practice_id
};

// Type for editing existing patients
export type EditPatientFormData = BasePatientFormData & {
    insurance_type: InsuranceType;
    insurance_name: string;
    pvs_approved: boolean;
};

export enum PatientCardType {
    SEARCH_RESULT = 'SEARCH_RESULT',
    CARD_READ = 'CARD_READ',
}
